// Study ledger (docs/HOME-2026-10.md, F1): local counts of study actions per day.
// Counts only. No titles, no addresses, nothing about what was opened. It lives in this
// browser (revault:days, revault:totals) and leaves the device only in the student's own
// backups (a save file, a carry code, the cloud backup) and, when they're signed in with study
// sync on, in their account's study sync copy (app/study-sync.js). There, two devices' totals
// merge by taking the larger count, not the sum (shared/merge-save.js). Shared by the app and the
// tools pages (same origin, same keys); other documents hear about changes through store.js
// (BroadcastChannel + storage events).
import { readJSON, writeJSON, remove } from "./store.js";
import { addCommons } from "./commons.js";
import { readSync } from "./sync-state.js";

export const DAYS_KEY = "revault:days";
export const TOTALS_KEY = "revault:totals";
// Per-day caps: anything above is ignored, so repeating an action for points does nothing.
// b books opened, t tools opened, fc cards marked known, td tasks done, fo focus sessions,
// fm focus minutes, n notes edited (once a day), fr flashcards reviewed (either answer).
export const CAPS = { b: 30, t: 30, fc: 200, td: 30, fo: 12, fm: 600, n: 1, fr: 300 };
const STUDY = ["b", "t", "fc", "td", "fo", "n", "fr"];
const KEEP = 70;
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

const p2 = (n) => (n < 10 ? "0" : "") + n;
// "2026-10-02", in local time.
export function dateKey(d = new Date()) { return d.getFullYear() + "-" + p2(d.getMonth() + 1) + "-" + p2(d.getDate()); }
// Local noon of that calendar day (immune to DST edges when stepping whole days).
const noon = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12);
export function fromKey(key) { const [y, m, d] = String(key).split("-").map(Number); return new Date(y, m - 1, d, 12); }
// Whole calendar days from key a to key b (b later = positive).
export function dayDiff(a, b) { return Math.round((fromKey(b) - fromKey(a)) / 864e5); }
// ISO 8601 week, Monday start, local time: "2026-W40".
export function weekKey(d = new Date()) {
	const t = noon(d);
	const dow = (t.getDay() + 6) % 7; // Monday 0 .. Sunday 6
	const thu = new Date(t.getFullYear(), t.getMonth(), t.getDate() - dow + 3, 12);
	const y = thu.getFullYear();
	const jan4 = new Date(y, 0, 4, 12);
	const mon1 = new Date(y, 0, 4 - ((jan4.getDay() + 6) % 7), 12);
	return y + "-W" + p2(1 + Math.round((thu - mon1) / (7 * 864e5)));
}
// Now, by the server's clock when study sync has found this one off (revault:sync offset). A day is
// recorded under the date the server would give it, so a clock days fast can't file study under a
// day that hasn't come yet, which would also slip past a stats reset made on another device.
export function clockNow() {
	let off = 0;
	try { off = Number(readSync().offset); } catch { off = 0; }
	return new Date(Date.now() - (Number.isFinite(off) ? off : 0));
}
export function mondayOf(d = new Date()) { const t = noon(d); return new Date(t.getFullYear(), t.getMonth(), t.getDate() - ((t.getDay() + 6) % 7), 12); }

// Recording stops in incognito and when the student turned history off. Reads the stored
// settings directly (defaults: both off), so the tools bundle needs no settings schema.
export function enabled() {
	const s = obj(readJSON("revault:settings", 0));
	return s.incognito !== true && s.keepHistory !== false;
}

const obj = (v) => (v && typeof v === "object" && !Array.isArray(v) ? v : {});
const count = (v) => (Number.isInteger(v) && v > 0 ? v : 0);
// Reads tolerate anything: unknown keys, wrong types and values above a cap are dropped.
export function days() {
	const raw = obj(readJSON(DAYS_KEY, 0)), out = {};
	for (const k of Object.keys(raw)) {
		if (!DAY_RE.test(k)) continue;
		const r = obj(raw[k]), c = r.v === 1 ? { v: 1 } : {};
		for (const n of Object.keys(CAPS)) if (count(r[n])) c[n] = Math.min(r[n], CAPS[n]);
		out[k] = c;
	}
	return out;
}
export function totals() {
	const raw = obj(readJSON(TOTALS_KEY, 0)), out = {};
	for (const k of [...Object.keys(CAPS), "days"]) out[k] = count(raw[k]);
	return out;
}
export function today(now = clockNow()) { return days()[dateKey(now)] || {}; }
export function isStudyDay(rec) { return !!rec && STUDY.some((k) => rec[k] > 0); }

// Monday..Sunday of that date's week: [{ key, rec }].
export function weekOf(date = new Date(), all = days()) {
	const m = mondayOf(date);
	const out = [];
	for (let i = 0; i < 7; i++) { const k = dateKey(new Date(m.getFullYear(), m.getMonth(), m.getDate() + i, 12)); out.push({ key: k, rec: all[k] || {} }); }
	return out;
}
// Sum of one count over a week.
export function weekSum(kind, date = new Date(), all = days()) { return weekOf(date, all).reduce((s, d) => s + (d.rec[kind] | 0), 0); }

function prune(all) {
	const keys = Object.keys(all).sort();
	for (let i = 0; i < keys.length - KEEP; i++) delete all[keys[i]];
	return all;
}

// Add n to today's count of kind. Returns how much was actually added (0 when capped,
// paused, or not a known kind).
export function bump(kind, n = 1, now = clockNow()) {
	if (!(CAPS[kind] > 0) || !count(n) || !enabled()) return 0; // CAPS[kind] > 0 also rejects inherited names
	const all = days();
	const k = dateKey(now);
	const rec = all[k] || {};
	const cur = rec[kind] | 0;
	const next = Math.min(CAPS[kind], cur + n);
	const add = next - cur;
	if (add <= 0) return 0;
	const wasStudy = isStudyDay(rec);
	rec[kind] = next;
	all[k] = rec;
	writeJSON(DAYS_KEY, prune(all));
	const t = totals();
	t[kind] += add;
	if (!wasStudy && isStudyDay(rec)) t.days += 1;
	writeJSON(TOTALS_KEY, t);
	if (kind === "fm") addCommons(add, now);
	return add;
}

// The start page was shown today. Not a study action (a visit alone never fills a day).
export function markVisit(now = clockNow()) {
	if (!enabled()) return;
	const all = days();
	const k = dateKey(now);
	if (all[k] && all[k].v === 1) return;
	all[k] = Object.assign(all[k] || {}, { v: 1 });
	writeJSON(DAYS_KEY, prune(all));
}

// "Welcome back": the first start-page view today, and the last visit before today was
// 3 or more days ago. A brand-new ledger is not a return.
export function isReturn(all, now = clockNow()) {
	const k = dateKey(now);
	if (all[k] && all[k].v === 1) return false;
	let last = null;
	for (const d in all) if (d < k && all[d].v === 1 && (!last || d > last)) last = d;
	return !!last && dayDiff(last, k) >= 3;
}

export function clearStats() { remove(DAYS_KEY); remove(TOTALS_KEY); }

// 50 -> "50 min", 190 -> "3 h 10 min", 60 -> "1 h".
export function fmtMinutes(m) {
	m = Math.max(0, m | 0);
	const h = Math.floor(m / 60), r = m % 60;
	if (!h) return r + " min";
	return r ? h + " h " + r + " min" : h + " h";
}
